import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { evaluateEventQuality } from '@fellowship/shared'
import {
  useAdminAllEvents,
  useAdminEvent,
  useAdminCreateEvent,
  useAdminUpdateEvent,
  useAdminDeleteEvent,
  useAdminRestoreEvent,
  useAdminScrapedEvents,
} from '../../hooks/useAdmin'
import { getEventType } from '../../components/ui/eventTypes'
import { ConfirmModal } from '../../components/ui/ConfirmModal'
import { AdminModal, adminFieldClass, toDatetimeLocal, fromDatetimeLocal } from '../../components/admin/AdminModal'
import { ConfBars } from '../../components/admin/AdminAtoms'
import { DataTable } from '../../components/admin/DataTable'
import ChurchSearchCombobox from '../../components/ui/ChurchSearchCombobox'
import type { ColumnDef } from '@tanstack/react-table'
import type { AdminChurch, AdminEvent } from '../../types/admin'

// ─── Constants ────────────────────────────────────────────────────────────────

const EVENT_TYPES = ['potluck', 'worship', 'study', 'social', 'outdoors', 'kids', 'service'] as const
const STATUSES = ['active', 'cancelled', 'completed'] as const
const VISIBILITIES = ['public', 'unlisted', 'invite_only'] as const
const SOURCES = ['claimed', 'scraped'] as const

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatAge(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const hours = Math.floor(ms / 3_600_000)
  if (hours < 1) return '<1h'
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

const MISSING_FIELD_LABELS: Record<string, string> = {
  title: 'Missing title',
  description: 'Missing description',
  addressText: 'Missing address',
}

function eventQuality(e: AdminEvent) {
  return evaluateEventQuality({
    title: e.title, description: e.description, addressText: e.addressText, startsAt: e.startsAt,
  })
}

function eventQualityScore(e: AdminEvent): number {
  const q = eventQuality(e)
  return 4 - q.missingFields.length - (q.isPastEvent ? 1 : 0)
}

function eventQualityReasons(e: AdminEvent): string[] {
  const q = eventQuality(e)
  const reasons = q.missingFields.map((f) => MISSING_FIELD_LABELS[f])
  if (q.isPastEvent) reasons.push('Past event')
  return reasons
}

/** Fully complete — title, description, address, future date. Safe to publish without a human look. */
function isEventClearlyComplete(e: AdminEvent): boolean {
  return eventQuality(e).autoPublishable
}

// ─── Event form ───────────────────────────────────────────────────────────────

type EventFormState = {
  title: string; eventType: string; description: string; addressText: string
  locationLat: string; locationLng: string; hostedByType: string; hostedById: string
  visibility: string; source: string; status: string; startsAt: string; endsAt: string
}

const emptyForm = (): EventFormState => ({
  title: '', eventType: 'potluck', description: '', addressText: '',
  locationLat: '45.52', locationLng: '-122.68', hostedByType: 'church',
  hostedById: '', visibility: 'public', source: 'claimed', status: 'active',
  startsAt: toDatetimeLocal(new Date(Date.now() + 7 * 86_400_000).toISOString()),
  endsAt: '',
})

function eventToForm(e: AdminEvent): EventFormState {
  return {
    title: e.title, eventType: e.eventType, description: e.description ?? '',
    addressText: e.addressText, locationLat: String(e.locationLat), locationLng: String(e.locationLng),
    hostedByType: e.hostedByType, hostedById: e.hostedById, visibility: e.visibility,
    source: e.source, status: e.status,
    startsAt: e.startsAt ? toDatetimeLocal(e.startsAt) : toDatetimeLocal(new Date().toISOString()),
    endsAt: e.endsAt ? toDatetimeLocal(e.endsAt) : '',
  }
}

// ─── Event review modal ───────────────────────────────────────────────────────

interface EventReviewModalProps {
  event: AdminEvent
  onClose: () => void
  onPublish: (churchId?: string) => void
  onDismiss: () => void
  publishing: boolean
  dismissing: boolean
}

function EventReviewModal({ event, onClose, onPublish, onDismiss, publishing, dismissing }: EventReviewModalProps) {
  const [reassigning, setReassigning] = useState(false)
  const [reassignChurch, setReassignChurch] = useState<AdminChurch | null>(null)
  const t = getEventType(event.eventType as Parameters<typeof getEventType>[0])

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  return (
    <div
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '32px 40px' }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--bg)', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '80vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 80px rgba(0,0,0,0.35)', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '18px 24px', borderBottom: '1px solid var(--line-soft)', flexShrink: 0 }}>
          <span style={{ padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600, background: t.soft, color: t.color }}>{t.label}</span>
          <h2 style={{ flex: 1, margin: 0, fontSize: 16, fontWeight: 600, letterSpacing: '-0.01em', color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.title}</h2>
          <button type="button" onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, border: '1px solid var(--line)', background: 'var(--surface)', cursor: 'pointer', fontSize: 14, color: 'var(--ink-2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>✕</button>
        </div>

        <div style={{ padding: '20px 24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <div>
              <div className="text-[11px] font-semibold text-muted mb-0.5 uppercase tracking-wide">Church</div>
              <div className="text-[13px] text-ink">{event.churchName ?? <span className="text-muted">—</span>}</div>
            </div>
            <div>
              <div className="text-[11px] font-semibold text-muted mb-0.5 uppercase tracking-wide">Date</div>
              <div className="text-[13px] text-ink">{event.startsAt ? formatDate(event.startsAt) : '—'}</div>
            </div>
            <div className="col-span-2">
              <div className="text-[11px] font-semibold text-muted mb-0.5 uppercase tracking-wide">Address</div>
              <div className="text-[13px] text-ink">{event.addressText || '—'}</div>
            </div>
            {event.description && (
              <div className="col-span-2">
                <div className="text-[11px] font-semibold text-muted mb-0.5 uppercase tracking-wide">Description</div>
                <div className="text-[13px] text-ink whitespace-pre-wrap">{event.description}</div>
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid var(--line-soft)', paddingTop: 14 }}>
            {reassigning ? (
              <div className="flex flex-col gap-3">
                <div className="text-[13px] font-medium text-ink">Reassign to church before publishing:</div>
                <ChurchSearchCombobox
                  selected={reassignChurch}
                  onSelect={(c) => setReassignChurch(c)}
                  onClear={() => setReassignChurch(null)}
                  placeholder="Search churches…"
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => { if (reassignChurch) onPublish(reassignChurch.id) }}
                    disabled={!reassignChurch || publishing}
                    className="px-3.5 py-2 rounded-lg border-0 bg-primary text-on-primary text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-50"
                  >
                    {publishing ? 'Publishing…' : 'Reassign & publish'}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setReassigning(false); setReassignChurch(null) }}
                    className="px-3.5 py-2 rounded-lg border border-line-soft bg-surface text-[13px] cursor-pointer font-sans"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => onPublish()}
                  disabled={publishing}
                  className="px-3.5 py-2 rounded-lg border-0 bg-primary text-on-primary text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-50"
                >
                  {publishing ? 'Publishing…' : '✓ Publish'}
                </button>
                <button
                  type="button"
                  onClick={() => setReassigning(true)}
                  className="px-3.5 py-2 rounded-lg border border-line-soft bg-surface text-[13px] cursor-pointer font-sans"
                >
                  ↔ Reassign & publish
                </button>
                <button
                  type="button"
                  onClick={onDismiss}
                  disabled={dismissing}
                  className="px-3.5 py-2 rounded-lg border border-danger-line bg-danger-soft text-danger-deep text-[13px] cursor-pointer font-sans disabled:opacity-50 ml-auto"
                >
                  {dismissing ? 'Dismissing…' : 'Dismiss'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Live tab columns ─────────────────────────────────────────────────────────

function liveEventColumns(
  onEdit: (e: AdminEvent) => void,
  onDelete: (e: AdminEvent) => void,
  onRestore: (id: string) => void,
  restorePending: boolean,
): ColumnDef<AdminEvent, unknown>[] {
  return [
    {
      accessorKey: 'title',
      header: 'Title',
      meta: { label: 'Title' },
      cell: ({ row }) => (
        <Link to={`/events/${row.original.id}`} className="no-underline block max-w-[240px]">
          <div className="text-primary font-semibold text-[13px] hover:underline truncate">{row.original.title}</div>
          <div className="text-[11px] text-muted mt-0.5 font-mono">{row.original.id.slice(0, 8)}…</div>
        </Link>
      ),
    },
    {
      accessorKey: 'eventType',
      header: 'Type',
      meta: { label: 'Type' },
      cell: ({ getValue }) => {
        const t = getEventType(getValue() as Parameters<typeof getEventType>[0])
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold" style={{ background: t.soft, color: t.color }}>
            <t.icon size={10} strokeWidth={2} /> {t.label}
          </span>
        )
      },
    },
    {
      id: 'host',
      header: 'Host',
      meta: { label: 'Host' },
      accessorFn: (row) => row.churchName ?? row.hostedById,
      cell: ({ row }) => {
        const e = row.original
        if (e.churchName) return <span className="text-ink-2 text-[12px]">{e.churchName}</span>
        return <span className="text-muted text-[12px] font-mono">{e.hostedById.slice(0, 8)}…</span>
      },
    },
    {
      id: 'hostType',
      header: 'Host Type',
      meta: { label: 'Host Type' },
      accessorFn: (row) => row.hostedByType,
      cell: ({ getValue }) => {
        const v = getValue() as string
        return <span className="text-ink-2 text-[12px] capitalize">{v}</span>
      },
    },
    {
      accessorKey: 'source',
      header: 'Source',
      meta: { label: 'Source' },
      cell: ({ getValue }) => <span className="text-ink-2 text-[12px]">{String(getValue())}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      meta: { label: 'Status' },
      cell: ({ getValue }) => {
        const s = getValue() as string
        return (
          <span className={`px-[7px] py-0.5 rounded-[5px] text-[11px] font-semibold ${s === 'active' ? 'text-[#1e7e34]' : 'bg-s2 text-muted'}`} style={s === 'active' ? { background: '#E6F4EA' } : undefined}>
            {s}
          </span>
        )
      },
    },
    {
      accessorKey: 'visibility',
      header: 'Visibility',
      meta: { label: 'Visibility' },
      cell: ({ getValue }) => <span className="text-muted text-[12px]">{String(getValue())}</span>,
    },
    {
      accessorKey: 'createdAt',
      header: 'Created',
      meta: { label: 'Created' },
      cell: ({ getValue }) => <span className="text-muted">{formatDate(String(getValue()))}</span>,
    },
    {
      id: 'actions',
      header: '',
      meta: { skipExport: true },
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const e = row.original
        return (
          <div className="flex gap-2">
            {e.deletedAt ? (
              <button type="button" onClick={() => onRestore(e.id)} disabled={restorePending} className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-good bg-good-soft text-good-deep disabled:opacity-50">Restore</button>
            ) : (
              <>
                <button type="button" onClick={() => onEdit(e)} className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-line-soft bg-s2 text-ink">Edit</button>
                <button type="button" onClick={() => onDelete(e)} className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-danger-line bg-danger-soft text-danger-deep">Delete</button>
              </>
            )}
          </div>
        )
      },
    },
  ]
}

// ─── Review tab columns ───────────────────────────────────────────────────────

function reviewEventColumns(
  onReview: (e: AdminEvent) => void,
  onPublish: (e: AdminEvent) => void,
  onDismiss: (e: AdminEvent) => void,
  publishingId: string | null,
  dismissingId: string | null,
): ColumnDef<AdminEvent, unknown>[] {
  return [
    {
      accessorKey: 'title',
      header: 'Title',
      meta: { label: 'Title' },
      cell: ({ row }) => (
        <button type="button" onClick={() => onReview(row.original)} className="text-primary font-semibold text-[13px] hover:underline border-0 bg-transparent cursor-pointer p-0 font-sans text-left max-w-[220px] block truncate">
          {row.original.title}
        </button>
      ),
    },
    {
      id: 'host',
      header: 'Host',
      meta: { label: 'Host' },
      accessorFn: (row) => row.churchName ?? null,
      cell: ({ getValue }) => {
        const v = getValue() as string | null
        return <span className={`text-[12px] ${v ? 'text-ink-2' : 'text-muted'}`}>{v ?? '—'}</span>
      },
    },
    {
      id: 'hostType',
      header: 'Host Type',
      meta: { label: 'Host Type' },
      accessorFn: (row) => row.hostedByType,
      cell: ({ getValue }) => {
        const v = getValue() as string
        return <span className="text-ink-2 text-[12px] capitalize">{v}</span>
      },
    },
    {
      accessorKey: 'eventType',
      header: 'Type',
      meta: { label: 'Type' },
      cell: ({ getValue }) => {
        const t = getEventType(getValue() as Parameters<typeof getEventType>[0])
        return (
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[11px] font-semibold" style={{ background: t.soft, color: t.color }}>
            <t.icon size={10} strokeWidth={2} /> {t.label}
          </span>
        )
      },
    },
    {
      id: 'date',
      header: 'Date',
      meta: { label: 'Date' },
      accessorFn: (row) => row.startsAt ?? null,
      cell: ({ getValue }) => {
        const v = getValue() as string | null
        return <span className="text-muted text-[12px]">{v ? formatDate(v) : '—'}</span>
      },
    },
    {
      accessorKey: 'addressText',
      header: 'Address',
      meta: { label: 'Address' },
      cell: ({ getValue }) => {
        const v = getValue() as string
        return <span className="text-ink-2 text-[12px] max-w-[160px] block truncate">{v || '—'}</span>
      },
    },
    {
      id: 'confidence',
      header: 'Confidence',
      meta: { label: 'Confidence' },
      accessorFn: (row) => eventQualityScore(row),
      cell: ({ getValue }) => <ConfBars score={getValue() as number} max={4} />,
    },
    {
      id: 'flags',
      header: 'Flags',
      meta: { label: 'Flags' },
      enableSorting: false,
      accessorFn: (row) => eventQualityReasons(row).join(','),
      cell: ({ row }) => {
        const reasons = eventQualityReasons(row.original)
        if (reasons.length === 0) return <span className="text-muted text-[12px]">—</span>
        return (
          <div className="flex flex-col gap-1">
            {reasons.map((r) => <span key={r} className="text-amber-700 text-[11px] font-medium">⚠ {r}</span>)}
          </div>
        )
      },
    },
    {
      id: 'age',
      header: 'Age',
      meta: { label: 'Age' },
      accessorFn: (row) => Date.now() - new Date(row.createdAt).getTime(),
      cell: ({ row }) => <span className="text-muted text-[12px] font-mono">{formatAge(row.original.createdAt)}</span>,
    },
    {
      id: 'actions',
      header: '',
      meta: { skipExport: true },
      enableSorting: false,
      enableHiding: false,
      cell: ({ row }) => {
        const e = row.original
        return (
          <div className="flex gap-1.5 justify-end">
            <button
              type="button"
              onClick={() => onPublish(e)}
              disabled={publishingId === e.id}
              className="px-2.5 py-1 rounded-md text-[12px] font-semibold cursor-pointer border-0 bg-primary text-on-primary font-sans disabled:opacity-50"
            >
              {publishingId === e.id ? '…' : '✓ Publish'}
            </button>
            <button
              type="button"
              onClick={() => onReview(e)}
              className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-line-soft bg-s2 text-ink"
            >
              Review
            </button>
            <button
              type="button"
              onClick={() => onDismiss(e)}
              disabled={dismissingId === e.id}
              className="px-2.5 py-1 rounded-md text-[12px] cursor-pointer font-sans border border-danger-line bg-danger-soft text-danger-deep disabled:opacity-50"
            >
              {dismissingId === e.id ? '…' : '✗'}
            </button>
          </div>
        )
      },
    },
  ]
}

// ─── Tab bar ──────────────────────────────────────────────────────────────────

type EventTab = 'live' | 'review'

function TabBar({ tab, onChange, reviewCount }: { tab: EventTab; onChange: (t: EventTab) => void; reviewCount: number }) {
  const tabs: { id: EventTab; label: string; count?: number }[] = [
    { id: 'live', label: 'Live' },
    { id: 'review', label: 'Review', count: reviewCount },
  ]
  return (
    <div className="flex gap-1 mb-5">
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          className="px-4 py-2 text-[13px] font-semibold rounded-t-lg cursor-pointer font-sans transition-colors bg-transparent"
          style={{ border: 'none', borderBottom: tab === t.id ? '2px solid var(--primary, #3B4CCA)' : '2px solid transparent', color: tab === t.id ? 'var(--primary)' : 'var(--ink-2)', marginBottom: -1 }}
        >
          {t.label}
          {t.count != null && t.count > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 text-amber-800">{t.count}</span>
          )}
        </button>
      ))}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function AdminEventsPage() {
  const [tab, setTab] = useState<EventTab>('live')

  // Live tab state
  const [showDeleted, setShowDeleted] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<AdminEvent | null>(null)
  const [editingId, setEditingId] = useState<string | 'new' | null>(null)
  const [form, setForm] = useState<EventFormState>(emptyForm)

  // Review tab state
  const [reviewingEvent, setReviewingEvent] = useState<AdminEvent | null>(null)
  const [publishingId, setPublishingId] = useState<string | null>(null)
  const [dismissingId, setDismissingId] = useState<string | null>(null)

  // Queries
  const { data: allEventsData, isLoading: liveLoading } = useAdminAllEvents({ showDeleted })
  const { data: reviewData, isLoading: reviewLoading } = useAdminScrapedEvents()
  const { data: detailData } = useAdminEvent(editingId && editingId !== 'new' ? editingId : null)

  const liveEvents = allEventsData?.data ?? []
  const total = allEventsData?.meta.total ?? 0
  const reviewEvents = useMemo(() => reviewData?.data ?? [], [reviewData])

  // Events already sitting in the queue (created before auto-publish, or by another
  // path) that turn out to be fully complete — safe to clear in one click.
  const autoSafeEvents = useMemo(() => reviewEvents.filter(isEventClearlyComplete), [reviewEvents])

  // Mutations
  const createEvent = useAdminCreateEvent()
  const updateEvent = useAdminUpdateEvent()
  const deleteEvent = useAdminDeleteEvent()
  const restoreEvent = useAdminRestoreEvent()

  useEffect(() => {
    if (editingId && editingId !== 'new' && detailData?.data) {
      setForm(eventToForm(detailData.data))
    }
  }, [editingId, detailData?.data])

  // ── Handlers ────────────────────────────────────────────────────────────────

  function openEdit(event: AdminEvent) {
    setForm(eventToForm(event))
    setEditingId(event.id)
  }

  function openCreate() {
    setForm(emptyForm())
    setEditingId('new')
  }

  function buildPayload() {
    const payload: Record<string, unknown> = {
      title: form.title.trim(), eventType: form.eventType,
      description: form.description || undefined, addressText: form.addressText.trim(),
      locationLat: parseFloat(form.locationLat), locationLng: parseFloat(form.locationLng),
      hostedByType: form.hostedByType, hostedById: form.hostedById.trim(),
      visibility: form.visibility, source: form.source, status: form.status,
      startsAt: fromDatetimeLocal(form.startsAt),
    }
    if (form.endsAt) payload.endsAt = fromDatetimeLocal(form.endsAt)
    return payload
  }

  function handleSave() {
    const payload = buildPayload()
    if (editingId === 'new') {
      createEvent.mutate(payload, { onSuccess: () => setEditingId(null) })
    } else if (editingId) {
      updateEvent.mutate({ id: editingId, ...payload }, { onSuccess: () => setEditingId(null) })
    }
  }

  function handlePublishInline(event: AdminEvent, churchId?: string) {
    setPublishingId(event.id)
    updateEvent.mutate(
      {
        id: event.id, source: 'claimed', status: 'active', visibility: 'public',
        ...(churchId ? { hostedByType: 'church', hostedById: churchId } : {}),
      },
      {
        onSuccess: () => {
          setPublishingId(null)
          if (reviewingEvent?.id === event.id) setReviewingEvent(null)
        },
        onError: () => setPublishingId(null),
      },
    )
  }

  function handleDismiss(event: AdminEvent) {
    setDismissingId(event.id)
    deleteEvent.mutate(event.id, {
      onSuccess: () => {
        setDismissingId(null)
        if (reviewingEvent?.id === event.id) setReviewingEvent(null)
      },
      onError: () => setDismissingId(null),
    })
  }

  function handleBulkPublish() {
    for (const e of autoSafeEvents) handlePublishInline(e)
  }

  const isPending = createEvent.isPending || updateEvent.isPending

  const liveCols = liveEventColumns(
    openEdit, (e) => setConfirmDelete(e),
    (id) => restoreEvent.mutate(id),
    restoreEvent.isPending,
  )

  const revCols = reviewEventColumns(
    (e) => setReviewingEvent(e),
    (e) => handlePublishInline(e),
    (e) => handleDismiss(e),
    publishingId,
    dismissingId,
  )

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="m-0 text-[24px] font-bold">Events</h1>
          <div className="text-[13px] text-muted mt-1">
            {tab === 'live' && `${total} total`}
            {tab === 'review' && `${reviewEvents.length} pending review`}
          </div>
        </div>
        {tab === 'live' && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setShowDeleted((v) => !v)}
              className={`h-9 px-3.5 rounded-lg border text-[13px] font-semibold cursor-pointer font-sans ${showDeleted ? 'bg-amber-50 border-amber-300 text-amber-800' : 'border-line-soft bg-surface text-ink-2'}`}
            >
              {showDeleted ? 'Hide deleted' : 'Show deleted'}
            </button>
            {!showDeleted && (
              <button
                type="button"
                onClick={openCreate}
                className="h-9 px-4 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans"
              >
                + Add event
              </button>
            )}
          </div>
        )}
        {tab === 'review' && autoSafeEvents.length > 0 && (
          <button
            type="button"
            onClick={handleBulkPublish}
            className="h-9 px-4 rounded-lg border-0 bg-[#22c55e] text-white text-[13px] font-semibold cursor-pointer font-sans"
          >
            Publish clearly complete ({autoSafeEvents.length})
          </button>
        )}
      </div>

      <TabBar tab={tab} onChange={setTab} reviewCount={reviewEvents.length} />

      {tab === 'live' && (
        <DataTable
          columns={liveCols}
          data={liveEvents}
          isLoading={liveLoading}
          sortable
          filterable
          configurable
          exportable
          exportFilename="fellowship-events"
          pageSize={50}
          emptyMessage="No events found."
        />
      )}

      {tab === 'review' && (
        <DataTable
          columns={revCols}
          data={reviewEvents}
          isLoading={reviewLoading}
          sortable
          filterable
          pageSize={50}
          emptyMessage="No scraped events pending review."
        />
      )}

      {/* Review modal */}
      {reviewingEvent && (
        <EventReviewModal
          event={reviewingEvent}
          onClose={() => setReviewingEvent(null)}
          onPublish={(churchId) => handlePublishInline(reviewingEvent, churchId)}
          onDismiss={() => handleDismiss(reviewingEvent)}
          publishing={publishingId === reviewingEvent.id}
          dismissing={dismissingId === reviewingEvent.id}
        />
      )}

      {/* Edit / create modal */}
      {editingId && (
        <AdminModal
          title={editingId === 'new' ? 'Add event' : 'Edit event'}
          onClose={() => setEditingId(null)}
          wide
          footer={
            <>
              <button type="button" onClick={() => setEditingId(null)} className="px-4 py-2 rounded-lg border border-line-soft bg-surface text-[13px] cursor-pointer font-sans">Cancel</button>
              <button type="button" onClick={handleSave} disabled={isPending || !form.title || !form.hostedById} className="px-4 py-2 rounded-lg border-0 bg-[#1a1a2e] text-white text-[13px] font-semibold cursor-pointer font-sans disabled:opacity-50">
                {isPending ? 'Saving…' : 'Save'}
              </button>
            </>
          }
        >
          <div className="grid grid-cols-2 gap-4">
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2 col-span-2">
              Title
              <input className={adminFieldClass()} value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Type
              <select className={adminFieldClass()} value={form.eventType} onChange={(e) => setForm((f) => ({ ...f, eventType: e.target.value }))}>
                {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Status
              <select className={adminFieldClass()} value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
                {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2 col-span-2">
              Address
              <input className={adminFieldClass()} value={form.addressText} onChange={(e) => setForm((f) => ({ ...f, addressText: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Lat
              <input className={adminFieldClass()} value={form.locationLat} onChange={(e) => setForm((f) => ({ ...f, locationLat: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Lng
              <input className={adminFieldClass()} value={form.locationLng} onChange={(e) => setForm((f) => ({ ...f, locationLng: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Hosted by type
              <select className={adminFieldClass()} value={form.hostedByType} onChange={(e) => setForm((f) => ({ ...f, hostedByType: e.target.value }))}>
                <option value="church">church</option>
                <option value="person">person</option>
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Hosted by ID (UUID)
              <input className={adminFieldClass()} value={form.hostedById} onChange={(e) => setForm((f) => ({ ...f, hostedById: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Visibility
              <select className={adminFieldClass()} value={form.visibility} onChange={(e) => setForm((f) => ({ ...f, visibility: e.target.value }))}>
                {VISIBILITIES.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Source
              <select className={adminFieldClass()} value={form.source} onChange={(e) => setForm((f) => ({ ...f, source: e.target.value }))}>
                {SOURCES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Starts at
              <input type="datetime-local" className={adminFieldClass()} value={form.startsAt} onChange={(e) => setForm((f) => ({ ...f, startsAt: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2">
              Ends at <span className="font-normal text-muted">(optional)</span>
              <input type="datetime-local" className={adminFieldClass()} value={form.endsAt} onChange={(e) => setForm((f) => ({ ...f, endsAt: e.target.value }))} />
            </label>
            <label className="flex flex-col gap-1 text-[12px] font-semibold text-ink-2 col-span-2">
              Description
              <textarea className={`${adminFieldClass()} min-h-[80px]`} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
            </label>
          </div>
        </AdminModal>
      )}

      {confirmDelete && (
        <ConfirmModal
          title="Delete event?"
          body={<><strong>{confirmDelete.title}</strong> will be soft-deleted and hidden from public search. It can be restored from the deleted events view.</>}
          confirmLabel="Delete"
          pendingLabel="Deleting…"
          onConfirm={() => deleteEvent.mutate(confirmDelete.id, { onSuccess: () => setConfirmDelete(null) })}
          onCancel={() => setConfirmDelete(null)}
          isPending={deleteEvent.isPending}
        />
      )}
    </div>
  )
}
